import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { ClipboardText, Play } from "@phosphor-icons/react";
import { fmtDate, fmtDateTime } from "../../lib/dateUtil";

/** My Work card: inspections due to me, ones I've started, start any active checklist, my recent results. */
export default function OperativeInspectionsCard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = useCallback(() => API.get("/in-house-portal/inspections").then(({ data }) => setD(data)).catch(() => setD({ due: [], templates: [], in_progress: [], recent: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (!d || (!d.due.length && !d.templates.length && !d.in_progress.length)) return null;

  const start = async (templateId, scheduleId, location) => {
    try { const { data } = await API.post("/hse/inspections/start", { template_id: templateId, schedule_id: scheduleId, location }); navigate(`/my-work/inspections/${data.id}`); } catch (e) { toast.error(formatApiError(e)); }
  };
  const tone = (s) => (s.overdue ? "bg-red-100 text-red-700" : s.due_soon ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700");

  return (
    <div className="bg-card border border-border rounded-lg p-5 border-l-4 border-l-sky-500" data-testid="operative-inspections-card">
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><ClipboardText className="h-5 w-5 text-sky-600" weight="fill" /> Inspections &amp; checks</h2>
      <div className="space-y-3">
        {d.in_progress.length > 0 && (
          <div data-testid="operative-insp-in-progress">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Carry on</p>
            {d.in_progress.map(i => (
              <button key={i.id} type="button" onClick={() => navigate(`/my-work/inspections/${i.id}`)} className="w-full flex items-center gap-2 text-xs py-1.5 px-2 rounded hover:bg-muted text-left" data-testid={`operative-insp-continue-${i.id}`}>
                <span className="font-medium flex-1">{i.title}</span><span className="text-muted-foreground">{i.score.answered}/{i.score.total} done</span>
              </button>
            ))}
          </div>
        )}
        {d.due.length > 0 && (
          <div data-testid="operative-insp-due">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Due</p>
            {d.due.map(s => (
              <div key={s.id} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded hover:bg-muted" data-testid={`operative-insp-due-${s.id}`}>
                <div className="flex-1"><span className="font-medium">{s.title}</span>{s.location && <span className="text-muted-foreground"> · {s.location}</span>}<div className="text-[11px] text-muted-foreground">{s.frequency_label}</div></div>
                <Badge className={`text-[10px] ${tone(s)}`}>{s.next_due ? (s.overdue ? "overdue · " : "due ") + fmtDate(s.next_due) : "done"}</Badge>
                <Button size="sm" className="h-7" onClick={() => start(s.template_id, s.id, s.location)} data-testid={`operative-insp-start-${s.id}`}><Play size={12} className="mr-1" /> Start</Button>
              </div>
            ))}
          </div>
        )}
        {d.templates.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="operative-insp-adhoc">
            <span className="text-[11px] text-muted-foreground">Start a check:</span>
            <select defaultValue="" onChange={e => { if (e.target.value) start(e.target.value); e.target.value = ""; }} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="operative-insp-adhoc-select">
              <option value="" disabled>Choose a checklist…</option>
              {d.templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </div>
        )}
        {d.recent.length > 0 && (
          <div data-testid="operative-insp-recent">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Recent</p>
            {d.recent.map(i => (
              <button key={i.id} type="button" onClick={() => navigate(`/my-work/inspections/${i.id}`)} className="w-full flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-muted text-left">
                <span className="flex-1">{i.title}</span><Badge className={`text-[10px] ${i.overall === "pass" ? "bg-emerald-100 text-emerald-700" : i.overall === "fail" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{i.overall}</Badge><span className="text-muted-foreground">{fmtDateTime(i.completed_at)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
